import { Building2, TrendingUp, Briefcase, Award, Mic, ArrowUpRight, Mail } from "lucide-react";
import { CornerMarks, StatusLED } from "@/components/infra";
import { openCalendly } from "@/lib/calendly";

const PHOTO_URL =
  "https://customer-assets.emergentagent.com/job_book-minimal-1/artifacts/zrbredxf_DSC_0211.jpeg";

const ROLES = [
  {
    Icon: Building2,
    title: "Founder",
    org: "Data Center Youngbloods",
    logo: "/dcyb-logo.png",
  },
  {
    Icon: TrendingUp,
    title: "Investment Analyst",
    org: "DPGlobal Assets",
    logo: "/dpg-logo.png",
    email: "La@dpglobalassets.com",
  },
  {
    Icon: Briefcase,
    title: "Partner & Financial Analyst",
    org: "Pura Vida Data Centers",
    logo: "/pvdc-logo.png",
    email: "La@pvdc.net",
  },
];

const CREDENTIALS = [
  { code: "DCIE", label: "Data Center Infrastructure Expert" },
  { code: "DCIS", label: "Data Center Infrastructure Specialist" },
  { code: "DCES", label: "Data Center Engineering Specialist" },
  { code: "PTC", label: "Beyond Academy Master Class" },
];

const FEATURES = [
  { show: "NEDAS Live", host: "with Ilissa Miller", url: "https://datacenterpost.com/building-the-next-generation-of-data-center-leaders-a-conversation-with-luke-adams/" },
  { show: "JSA TV", host: "Datacloud Talent in Tech", url: "https://www.youtube.com/watch?v=pesI5QRkhOY" },
];

export default function Founder() {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="border-b hairline bg-[#F4F5F7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-16">
          <div className="col-span-12 lg:col-span-5">
            <div data-testid="founder-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6 flex items-center gap-3">
              <StatusLED />
              / Built by
            </div>
            <h2
              data-testid="founder-headline"
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight font-extrabold text-[#0F1117] text-balance"
            >
              Luke <span className="text-chrome">Adams.</span>
            </h2>
            <p className="mt-6 font-mono-label text-[11px] text-[#5F6168]">
              Founder · Operator · Allocator
            </p>
          </div>

          {/* Photo card */}
          <div className="col-span-12 lg:col-span-7 lg:pl-8 flex lg:justify-end">
            <div className="chrome-surface relative p-2 w-full max-w-[360px]">
              <CornerMarks color="#A9ADB4" />
              <div className="relative bg-[#ECEDEF]">
                <img
                  src={PHOTO_URL}
                  alt="Luke Adams"
                  className="w-full h-auto object-contain block"
                  draggable="false"
                />
                {/* Caption strip */}
                <div className="absolute bottom-0 inset-x-0 bg-white/90 backdrop-blur-sm border-t hairline px-4 py-2.5 flex items-center justify-between">
                  <span className="font-mono-label text-[9px] text-[#5F6168]">
                    REF · LA-001
                  </span>
                  <span className="font-mono-label text-[9px] text-[#0F1117] flex items-center gap-1.5">
                    <StatusLED />
                    On the network
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bio + Sidebar */}
        <div className="grid grid-cols-12 gap-y-12 gap-x-6">
          {/* Bio */}
          <div className="col-span-12 lg:col-span-7 space-y-6 text-base sm:text-[17px] text-[#3D4049] leading-[1.75]">
            <p data-testid="founder-bio-introduction">
              Luke Adams is the{" "}
              <strong className="text-[#0F1117] font-semibold">
                Founder of Data Center Youngbloods
              </strong>{" "}
              and an Investment Analyst at{" "}
              <strong className="text-[#0F1117] font-semibold">DPGlobal Assets</strong>,
              where he works hands-on across the full financial and technical lifecycle
              of digital infrastructure projects, from due diligence through the
              detailed analysis behind AI infrastructure development.
            </p>
            <p>
              He holds the complete{" "}
              <strong className="text-[#0F1117] font-semibold">
                IDCA certification stack, DCIE, DCIS, and DCES
              </strong>
              , and completed the{" "}
              <strong className="text-[#0F1117] font-semibold">
                PTC Beyond Academy Master Class
              </strong>
              , deepening his expertise across power, cooling, sustainability, and the
              global digital infrastructure landscape. As a Partner and Financial
              Analyst at{" "}
              <strong className="text-[#0F1117] font-semibold">
                Pura Vida Data Centers
              </strong>
              , he led the research and built the investor presentation for the
              project, which is in pre-development as it advances toward its vision of
              bringing AI infrastructure to Costa Rica, and he presents the opportunity
              directly to investors.
            </p>
            <p>
              Luke has spent the last two years immersed in the industry from the
              inside, engaging directly with its leaders at the events shaping its
              future. He has become a recognized voice for the next generation, sharing
              his perspective on{" "}
              <strong className="text-[#0F1117] font-semibold">
                NEDAS Live with Ilissa Miller
              </strong>{" "}
              and on{" "}
              <strong className="text-[#0F1117] font-semibold">
                JSA TV through the Datacloud Talent in Tech program
              </strong>
              , where he spoke about what drew him into digital infrastructure, why
              curiosity is the most important skill a young professional can bring, and
              his vision for a more sustainable, human-centered industry.
            </p>
            <p className="text-[#52555B]">
              Alongside his co-founder{" "}
              <strong className="text-[#0F1117] font-semibold">JohnPaul Adams</strong>,
              Luke founded Data Center Youngbloods to{" "}
              <span className="text-chrome font-semibold">close the workforce gap</span>{" "}
              in the biggest infrastructure buildout of our lifetime. The mission is
              direct. Connect the next generation to careers building the systems that
              power the AI era.
            </p>

            {/* Inline CTA */}
            <div className="pt-4 flex flex-wrap gap-3">
              <a
                href="#book"
                onClick={openCalendly}
                data-testid="founder-book-btn"
                className="chrome-btn-dark inline-flex items-center gap-2 font-mono-label text-[12px] px-6 py-4"
              >
                Meet with Luke
                <ArrowUpRight size={16} />
              </a>
              <a
                href="https://www.linkedin.com/in/luke-adams-dcyb/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-linkedin-btn"
                className="chrome-btn inline-flex items-center gap-2 font-mono-label text-[12px] px-6 py-4"
              >
                LinkedIn
                <ArrowUpRight size={16} />
              </a>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="col-span-12 lg:col-span-5 lg:pl-8 space-y-6">
            {/* Roles */}
            <div className="chrome-surface relative">
              <div className="px-5 py-3 border-b hairline">
                <span className="font-mono-label text-[10px] text-[#5F6168]">
                  / Active Roles
                </span>
              </div>
              <ul>
                {ROLES.map(({ title, org, logo, email }) => (
                  <li
                    key={title}
                    data-testid={`founder-role-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="px-5 py-4 border-b hairline last:border-b-0 flex items-start gap-4 hover:bg-[#F4F5F7] transition-colors"
                  >
                    <span className="chrome-tile w-11 h-11 flex items-center justify-center shrink-0 bg-white border hairline overflow-hidden">
                      <img
                        src={logo}
                        alt=""
                        aria-hidden
                        className="max-w-[78%] max-h-[78%] object-contain"
                        draggable="false"
                      />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-[15px] font-bold text-[#0F1117] leading-tight">
                        {title}
                      </div>
                      <div className="text-[13px] text-[#52555B] mt-0.5">{org}</div>
                      {email && (
                        <a
                          href={`mailto:${email}`}
                          className="mt-1.5 group inline-flex items-center gap-1.5 text-[12px] text-[#0F1117] hover:text-[#22C55E] transition-colors"
                        >
                          <Mail size={11} className="shrink-0" />
                          <span className="truncate">{email}</span>
                          <ArrowUpRight
                            size={11}
                            className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          />
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Credentials */}
            <div className="chrome-surface relative">
              <div className="px-5 py-3 border-b hairline flex items-center gap-2">
                <Award size={14} className="text-[#0F1117]" />
                <span className="font-mono-label text-[10px] text-[#5F6168]">
                  / Credentials
                </span>
              </div>
              <ul>
                {CREDENTIALS.map(({ code, label }) => (
                  <li
                    key={code}
                    data-testid={`founder-credential-${code.toLowerCase()}`}
                    className="px-5 py-4 border-b hairline last:border-b-0 flex items-center gap-5 hover:bg-[#F4F5F7] transition-colors"
                  >
                    <span className="font-display text-2xl font-extrabold text-chrome tracking-tight w-14">
                      {code}
                    </span>
                    <span className="text-[13px] text-[#3D4049] leading-snug">
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Featured On */}
            <div className="chrome-surface relative">
              <div className="px-5 py-3 border-b hairline flex items-center gap-2">
                <Mic size={14} className="text-[#0F1117]" />
                <span className="font-mono-label text-[10px] text-[#5F6168]">
                  / Featured On
                </span>
              </div>
              <ul>
                {FEATURES.map(({ show, host, url }) => {
                  const inner = (
                    <>
                      <div>
                        <div className="font-display text-[15px] font-bold text-[#0F1117] flex items-center gap-1.5">
                          {show}
                          {url && (
                            <ArrowUpRight
                              size={13}
                              className="text-[#71747B] group-hover:text-[#0F1117] transition-colors"
                            />
                          )}
                        </div>
                        <div className="text-[13px] text-[#52555B] mt-0.5">{host}</div>
                      </div>
                    </>
                  );
                  return (
                    <li
                      key={show}
                      data-testid={`founder-feature-${show.toLowerCase().replace(/\s+/g, "-")}`}
                      className="border-b hairline last:border-b-0 hover:bg-[#F4F5F7] transition-colors"
                    >
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group block px-5 py-4"
                        >
                          {inner}
                        </a>
                      ) : (
                        <div className="group px-5 py-4">{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
